<template>
    <div class="sms-send">
        <div class="header">
            <h1>Skapa sms</h1>
            <button type="button" @click="openSmsMallarTab">
                <img src="@/assets/icons/gear_icon.svg" alt="Hantera sms-mallar" />
                Hantera sms-mallar
            </button>
        </div>
        <div class="container-select-sms">
            <KAutocomplete
                v-if="smsMallar.length"
                label="Sms-mall"
                name="sms-mall"
                :options="smsMallar"
                @option:selected="onSmsMallOptionSelected"
                @inputValue="onSmsMallInput"
                validation="required"
            />
            <Button variant="standard" size="secondary" :disabled="isSelectedSmsMallEmpty" @click="showPreview">
                Skapa förhandvisning
            </Button>
        </div>

        <div v-if="hasAnyReceivers" class="sms-receiver-container">
            <span class="valid-receivers-count">
                Sms kommer att skickas till <strong>{{ validReceivers.length }}</strong> av
                <strong>{{ parameters?.receivers?.length }}</strong> valda patienter
            </span>
            <p v-if="hasObokade" class="obokade">
                <img src="@/assets/warning_dark.svg" alt="Varning" />
                <span> En eller flera operationer är ej bokade</span>
            </p>
            <div
                v-for="{ patient_display } in getReceiversWithoutPhoneNumber"
                :key="patient_display.id"
                class="missing-numbers"
            >
                <img src="@/assets/warning_dark.svg" alt="Varning" />
                <span>
                    Sms till
                    <strong>{{ patient_display?.personId_formatted ?? "" }} {{ patient_display?.namn ?? "" }}</strong>
                    kommer inte skickas
                    {{
                        patient_display?.onskar_sms
                            ? "eftersom mobilnummer saknas"
                            : "eftersom patienten inte vill ha sms"
                    }}
                </span>
            </div>
        </div>
        <div v-if="!isSelectedSmsMallEmpty && isPreviewDispayed" class="preview-sms">
            <h3>Förhandsvisning sms</h3>
            <hr />
            <AdvancedEditor
                simple-editor
                :content="selectedSmsMall.body"
                :fras-values="frasValues"
                @update:content="updateContent"
                type-label="Sms"
                :anmalan-patient-map="anmalanPatientMap"
            />
            <hr />
            <template>
                <ButtonGroup>
                    <Button
                        type="button"
                        size="primary"
                        @click="sendSms"
                        :disabled="!validReceivers.length || hasObokade"
                    >
                        Skicka sms
                        <span v-if="validReceivers.length">
                            &nbsp;({{ validReceivers.length }} av {{ parameters?.receivers?.length }})
                        </span>
                    </Button>
                    <Button type="button" size="secondary" variant="contour" @click="cancel"> Avbryt</Button>
                </ButtonGroup>
            </template>
        </div>
    </div>
</template>

<script>
    import { getConfig, klinikenApi } from "@/api";
    import Button from "@/components/Button.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import AdvancedEditor from "@/components/Editor/AdvancedEditor.vue";
    import { mapAnmalanPatients, valideraFraser } from "@/components/Editor/helpers";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";

    import { useStore } from "@/store";
    import tabEvents from "@/tabEvents";
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import SMSMallar from "@/tabs/Operation/SMS/SMSMallar.vue";
    import { flashMessage, flashWarning } from "@/utils";
    import { computed, onBeforeMount, onMounted, ref, watch } from "vue";

    export default {
        extends: BaseTab,
        name: "SMSSend",
        tabName: "Skapa sms",
        patientBound: true,
        components: { AdvancedEditor, Button, ButtonGroup, KAutocomplete },
        props: {
            tabId: Number,
            parameters: Object,
        },
        data() {
            return {
                content: "",
                errors: null,
                smsMallar: [],
                selectedSmsMall: {},
                isPreviewDispayed: false,
                successfulSms: [],
                failedSms: {}, // dictionary med { felmeddelande: antal }
            };
        },
        setup(context) {
            const store = useStore();

            const frasValues = ref(new Map());
            const anmalningar = computed(() =>
                store.getters["operation/operationsanmalan/getOpanmalningarningarByIds"](
                    context.parameters?.receivers?.map(({ id }) => id) ?? []
                )
            );

            const hasObokade = computed(() => {
                return anmalningar.value.find((anmalan) => anmalan.status !== "Bokad" && !anmalan.preliminarbokning);
            });

            const callValideraFraser = async () => {
                frasValues.value = await valideraFraser({
                    anmalanIds: context.parameters.receivers?.map(({ id }) => id) ?? [],
                });
            };

            onBeforeMount(async () => {
                await callValideraFraser();
            });

            onMounted(() => {
                watch(anmalningar, async () => {
                    await callValideraFraser();
                });
            });

            return {
                anmalningar,
                frasValues,
                hasObokade,
            };
        },
        computed: {
            /**
             * Checks if the selectedSmsMall object is empty.
             * @returns {boolean} Returns true if the selectedSmsMall object is empty, otherwise false.
             */
            isSelectedSmsMallEmpty() {
                return Object.keys(this.selectedSmsMall).length === 0;
            },

            /**
             * Returns an array of Sms receivers that have a phone number and no errors.
             * @returns {Array} An array of Sms receivers with phone numbers and no errors.
             */
            validReceivers() {
                return this.getReceiversWithPhoneNumber.filter((receiver) => {
                    return (
                        (!this.errors || !this.errors.has(receiver.id)) &&
                        (receiver.status === "Bokad" || receiver.preliminarbokning)
                    );
                });
            },
            /**
             * Checks if there are any Sms receivers available?
             * @returns {boolean} Returns true if there are any Sms receivers, false otherwise.
             */
            hasAnyReceivers() {
                return this.parameters?.receivers?.length > 0;
            },

            /**
             * Returns an array of Sms receivers that have a phone number.
             *
             * @returns {Array} An array of Sms receivers with phone numbers.
             */
            getReceiversWithPhoneNumber() {
                return (
                    this.parameters?.receivers?.filter(({ patient_display }) => {
                        return (
                            patient_display?.telefon !== null &&
                            patient_display?.telefon !== undefined &&
                            patient_display?.telefon !== "" &&
                            patient_display?.onskar_sms !== false
                        );
                    }) || []
                );
            },

            /**
             * Returns an array of Sms receivers without a phone number.
             *
             * @returns {Array} An array of Sms receivers without phone numbers.
             */
            getReceiversWithoutPhoneNumber() {
                return (
                    this.parameters?.receivers?.filter(({ patient_display }) => {
                        return (
                            patient_display?.telefon === null ||
                            patient_display?.telefon === undefined ||
                            patient_display?.telefon === "" ||
                            patient_display?.onskar_sms === false
                        );
                    }) || []
                );
            },
        },
        methods: {
            updateContent({ data, errors: contentErrors }) {
                this.content = data;
                this.errors = contentErrors;
            },
            showPreview() {
                this.isPreviewDispayed = true;
            },
            onSmsMallOptionSelected(selectedOption = {}) {
                this.selectedSmsMall = selectedOption;
            },

            onSmsMallInput(input = null) {
                if (!input || input?.value === null) {
                    this._resetValues();
                }
            },

            openSmsMallarTab() {
                this.$store.dispatch("tabs/openTab", {
                    component: SMSMallar,
                });
            },

            async sendSms() {
                await Promise.all(
                    this.validReceivers.map(async ({ id }) => {
                        if (id) {
                            try {
                                const CONFIGERROR = "Sms kunde ej skickas, felaktig konfiguration.";
                                const { id: mall } = this.selectedSmsMall;
                                const body = this.content;
                                const baseUrl = this.parameters?.baseUrl ?? "";
                                const path = this.parameters?.path ?? "";

                                if (!baseUrl || !path) {
                                    if (CONFIGERROR in this.failedSms) {
                                        this.failedSms[CONFIGERROR] = this.failedSms[CONFIGERROR] + 1;
                                    } else {
                                        this.failedSms[CONFIGERROR] = 1;
                                    }
                                } else {
                                    const response = await klinikenApi.post(
                                        baseUrl + id + path,
                                        { mall, body },
                                        getConfig()
                                    );
                                    if (response.status === 201) {
                                        this.successfulSms.push({ ...response, anmalanId: id });
                                    } else {
                                        if (response.data.detail in this.failedSms) {
                                            this.failedSms[response.data.detail] =
                                                this.failedSms[response.data.detail] + 1;
                                        } else {
                                            this.failedSms[response.data.detail] = 1;
                                        }
                                    }
                                }
                            } catch (error) {
                                if (error.response.data.detail in this.failedSms) {
                                    this.failedSms[error.response.data.detail] =
                                        this.failedSms[error.response.data.detail] + 1;
                                } else {
                                    this.failedSms[error.response.data.detail] = 1;
                                }
                            }
                        }
                    })
                );

                if (this.successfulSms.length > 0) {
                    flashMessage(`Sms skickade (${this.successfulSms.length})`, 2000);

                    const filteredSms = new Set(this.successfulSms.map((data) => data.anmalanId));

                    const result = await Promise.all(
                        [...filteredSms.values()].map(async (anmalanId) => {
                            try {
                                const { data } = await klinikenApi.get(`operation/anmalan/${anmalanId}`, getConfig());

                                this.$store.commit("operation/operationsanmalan/setOpAnmala", data);
                                this.$store.commit("operation/operationsanmalanV2/updateOperationsanmalningar", {
                                    subsetName: "newlyUpdated",
                                    operationsanmalningar: [
                                        {
                                            ...data,
                                            storeInfo: {
                                                partial: false,
                                            },
                                        },
                                    ],
                                    offset: Object.keys(
                                        this.$store.state.operation["operationsanmalanV2"].subsets.newlyUpdated.ids
                                    ).length,
                                    freezeAllSubsets: true,
                                });
                            } catch (error) {
                                throw Error(error);
                            }
                        })
                    );

                    if (result?.state === "rejected") {
                        flashMessage(
                            "Kunde inte uppdatera listor. Stäng och öppna tabbarna som innehåller listor med operationsanmälan",
                            6000
                        );
                    }
                }

                for (let meddelande in this.failedSms) {
                    if (this.failedSms[meddelande] > 0) {
                        flashWarning(`${meddelande} (${this.failedSms[meddelande]})`, 2000);
                    }
                }

                tabEvents.$emit("refreshSmsKallelser", {});
                await this.$store.dispatch("tabs/closeTab", this.tabId);
            },

            async cancel() {
                await this.$store.dispatch("tabs/closeTab", this.tabId);
            },

            _resetValues() {
                this.selectedSmsMall = {};
                this.isPreviewDispayed = false;
                this.content = "";
            },

            _setSelectedSmsMall(selectedSmsMall = {}) {
                this.selectedSmsMall = selectedSmsMall;
            },

            async _fetchSmsMallar() {
                try {
                    const response = await klinikenApi.get(
                        "/sms/mallar/",
                        getConfig({
                            params: {
                                typ: this.parameters?.typ ?? "",
                            },
                        })
                    );

                    this.smsMallar = response.data.results.map((item) => {
                        return { value: item.id, label: item.namn, ...item };
                    });
                } catch (error) {
                    throw new Error(error);
                }
            },
        },

        async created() {
            this.frasValues = await valideraFraser({
                anmalanIds: this.parameters.receivers?.map(({ id }) => id) ?? [],
            });
            this.anmalanPatientMap = mapAnmalanPatients(
                this.parameters.receivers.map((anmalan) => ({
                    id: anmalan.id,
                    patient_display: anmalan.patient_display,
                })) ?? []
            );

            await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: true });

            await this._fetchSmsMallar();

            tabEvents.$on("refreshSmsMallar", this._fetchSmsMallar);

            await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });
        },
    };
</script>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";

    .sms-send {
        width: $content-width;
        max-width: 100%;

        .header {
            display: flex;
            align-items: center;
            gap: 40px;
            margin-bottom: 26px;

            h1 {
                margin: 0 !important;
            }

            .button-hollow:hover {
                background-color: transparent;
            }

            button {
                background: transparent;
                border: none;
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                    margin-right: 8px;
                }
            }
        }

        .container-select-sms {
            background-color: $color-blue-light;
            padding-top: 16px;
            padding-bottom: 25px;
            padding-left: 21px;
            padding-right: 21px;
            display: flex;
            column-gap: 40px;
            justify-content: center;
            align-items: center;

            button {
                flex-grow: 0;
            }
        }

        .container-select-sms > * {
            flex-grow: 1;
        }

        .sms-receiver-container {
            margin-top: 10px;
            margin-bottom: 20px;
            display: grid;

            .obokade,
            .missing-numbers {
                display: flex;
                align-items: center;

                img {
                    margin-right: 8px;
                }
            }

            .missing-numbers {
                margin-top: 10px;
                margin-bottom: 10px;
            }
        }

        .preview-sms {
            margin-top: 16px;

            div {
                margin: 16px 0px;

                p {
                    white-space: pre-line;
                }
            }

            .button-group {
                margin-top: 34px;
            }
        }
    }
</style>
